// @ts-nocheck
import { DashboardLayout } from "./Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Users,
  Activity,
  Calendar,
  TrendingUp,
  HeartHandshake,
  Mail,
  Phone,
  Building,
  Eye,
} from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router";
import { useAuth } from "@/contexts/use-auth";
import { useOrg } from "@/contexts/useOrg";
import { useGetData } from "@/hooks/useGetData";
import LoaderWrapper from "./LoaderWrapper";
import {
  DashboardcardShimmer,
  HorizontalCardShimmer,
} from "@/components/Shimmer";
import { useEffect, useState } from "react";
import { Loader } from "three/src/Three.Core.js";
import clsx from "clsx";
import TableWithSearch from "@/components/TableWithSearch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { careplanHeaders, treatmentPlanHeaders } from "@/utils/tablesData";
import { formatUSPhoneNumber } from "@/utils/helpers";

// Dummy data to replace Convex queries for now
const mockPatients = [
  { id: "1", status: "active", lastContactDate: "2025-11-01" },
  { id: "2", status: "pending", lastContactDate: "2025-10-28" },
  { id: "3", status: "inactive", lastContactDate: "2025-10-10" },
];

const mockCaretakers = [
  {
    _id: "c1",
    firstName: "John",
    lastName: "Doe",
    status: "active",
    email: "john@example.com",
    phone: "+91 99999 99999",
    patientCount: 4,
  },
];

export default function DashboardHome() {
  const navigate = useNavigate();
  const { user, displayName } = useAuth();

  // Redirect patients to their dedicated dashboard
  useEffect(() => {
    if (user?.role === "patient") {
      navigate("/patient-dashboard");
    }
  }, [user?.role, navigate]);

  return (
    <DashboardLayout activePage="/dashboard">
      <DashboardHomeContent />
    </DashboardLayout>
  );
}

function DashboardHomeContent() {
  const navigate = useNavigate();
  const { user, displayName, caretaker, isAuthorized } = useAuth();
  const { selectedOrgId, organizations, isAllSelected } = useOrg();
  const selectedOrganization = isAllSelected
    ? "Antro-care (all)"
    : organizations.find((o) => o._id == selectedOrgId);
  const IS_PORTAL_ACCESS = user?.role == "portal_access";
  const {
    apiData: stat,
    loading: statsLoading,
    err: statsErr,
  } = useGetData(
    "/dashboard/stats",
    { orgId: selectedOrgId },
    false,
    !!selectedOrgId && !IS_PORTAL_ACCESS,
  );
  const statsData = stat?.data;
  const {
    apiData,
    loading: caretakerLoading,
    err: caretakerError,
  } = useGetData(
    "/caretakers",
    { orgId: selectedOrgId },
    false,
    !!(isAuthorized("canViewCaretakerManagement") && selectedOrgId) &&
    !IS_PORTAL_ACCESS,
  );
  const caretakers = apiData?.data?.caretakers;

  const [careplans, setCareplans] = useState([]);
  const [treatmentPlans, setTreatmentPlans] = useState([]);
  const [portalTab, setPortalTab] = useState<"care-plan" | "treatment-plan">("care-plan");
  const [portalPatientIds, setPortalPatientIds] = useState<string[]>([]);

  // Get portal access (patient IDs) for logged-in user, then pass to treatment plan API
  // API returns: { data: { portalAccess: [ { patientId: { _id: "..." } }, ... ], total } }
  useGetData(
    "/portal-access/my-access",
    {},
    false,
    IS_PORTAL_ACCESS && !!user,
    (err, data: any) => {
      if (data?.data?.portalAccess && !err) {
        const ids = (data.data.portalAccess || [])
          .map((p: any) => p.patientId?._id)
          .filter(Boolean);
        setPortalPatientIds(ids);
      }
    }
  );
  // const {apiData : carePlanData, loading : carePlanLoading , err : carePlanErr} = useGetData("/portal-access/shared-careplans", {}, false, IS_PORTAL_ACCESS && user  )

  // Temporary mocked data (you’ll replace this with your API)
  const patients = mockPatients;
  // const caretakers = mockCaretakers;

  // Stats logic (same as before)
  const stats =
    isAuthorized("canViewInactivePatients") && user?.role !== "admin"
      ? [
        {
          title: "My Total Patients",
          value: statsData?.["totalPatients"] || 0,
          icon: Users,
          color: "text-blue-600",
          bgColor: "bg-blue-50",
          clickable: true,
          navigateTo: "/patients",
        },
        {
          title: "Active Patients",
          value: statsData?.["activePatients"] || 0,
          icon: Activity,
          color: "text-green-600",
          bgColor: "bg-green-50",
          clickable: true,
          filterStatus: "active",
          navigateTo: "/patients?status=active",
        },
        {
          title: "Pending Patients",
          value: statsData?.["pendingPatients"] || 0,
          icon: Calendar,
          color: "text-yellow-600",
          bgColor: "bg-yellow-50",
          clickable: true,
          filterStatus: "pending",
          navigateTo: "/patients?status=pending",
        },
        // {
        //   title: "Inactive Patients",
        //   value: statsData?.["inactivePatients"] || 0,
        //   icon: TrendingUp,
        //   color: "text-gray-600",
        //   bgColor: "bg-gray-50",
        //   clickable: false,
        // },
        {
          title: "Other Patients",
          value: (statsData?.["totalPatients"] || 0) - ((statsData?.["activePatients"] || 0) + (statsData?.["pendingPatients"]) || 0),
          icon: Users,
          color: "text-green-600",
          bgColor: "bg-green-50",
          clickable: true,
          filterStatus: "active",
          navigateTo: "/patients?refilter=other",
        },
      ]
      : [
        ...(isAllSelected
          ? [
            {
              title: "Total Organizations",
              value: statsData?.["totalOrganizations"] ?? organizations?.length ?? 0,
              icon: Building,
              color: "text-blue-600",
              bgColor: "bg-blue-50",
              clickable: true,
              navigateTo: "/organizations",
            },
          ]
          : []),
        {
          title: "Total Patients",
          value: statsData?.["totalPatients"] || 0,
          icon: Users,
          color: "text-blue-600",
          bgColor: "bg-blue-50",
          clickable: true,
          navigateTo: "/patients",
        },
        {
          title: "Active Patients",
          value: statsData?.["activePatients"] || 0,
          icon: Activity,
          color: "text-green-600",
          bgColor: "bg-green-50",
          clickable: true,
          filterStatus: "active",
          navigateTo: "/patients?status=active",
        },
        {
          title: "Pending Patients",
          value: statsData?.["pendingPatients"] || 0,
          icon: Calendar,
          color: "text-yellow-600",
          bgColor: "bg-yellow-50",
          clickable: true,
          filterStatus: "pending",
          navigateTo: "/patients?status=pending",
          stateData: { patientsView: "eligible" },
        },
        // {
        //   title: "Patients Onboarded This Month",
        //   value: statsData?.["monthlyPatients"] || 0,
        //   icon: TrendingUp,
        //   color: "text-purple-600",
        //   bgColor: "bg-purple-50",
        //   clickable: false,
        // },
        // {
        //   title: "Inactive Patients",
        //   value: statsData?.["inactivePatients"] || 0,
        //   icon: TrendingUp,
        //   color: "text-gray-600",
        //   bgColor: "bg-gray-50",
        //   navigateTo: "/patients?status=pending",
        //   clickable: true,
        // },
        {
          title: "Other Patients",
          value: (statsData?.["totalPatients"] || 0) - ((statsData?.["activePatients"] || 0) + (statsData?.["pendingPatients"]) || 0),
          icon: Users,
          color: "text-green-600",
          bgColor: "bg-green-50",
          clickable: true,
          filterStatus: "active",
          navigateTo: "/patients?refilter=other",
        },
      ];

  if (user?.role == "portal_access") {
    return (
      <div className="space-y-4">
        <Tabs value={portalTab} onValueChange={(v) => setPortalTab(v as "care-plan" | "treatment-plan")}>
          <TabsList>
            <TabsTrigger value="care-plan">Care Plan</TabsTrigger>
            <TabsTrigger value="treatment-plan">Treatment Plan</TabsTrigger>
          </TabsList>
          <TabsContent value="care-plan" className="mt-4">
            <TableWithSearch
              apiDataKey="carePlans"
              isColumnSelection
              columnPreferenceKey="portal-shared-careplans"
              columnHeaders={careplanHeaders}
              data={careplans || []}
              viewData={careplans.map((v: any) => ({
                ...v,
                patientId: `${v?.patientId?.firstName} ${v?.patientId?.lastName}`,
                careTakerId: `${v?.careTakerId?.firstName} ${v?.careTakerId?.lastName}`,
                plan: v?.carePlanType || v?.plan || v?.planType || v?.type || "",
              }))}
              setData={setCareplans}
              fetchCondition={IS_PORTAL_ACCESS && user && portalTab === "care-plan"}
              isIncludeSearch
              searchPlaceholder="Search Shared Careplans..."
              filters={[]}
              filtersOptions={[]}
              setFilters={() => { }}
              heading="Shared Careplans"
              isActionColumn={false}
              handleAction={() => { }}
              moreActions={[
                {
                  label: "View",
                  icon: <Eye className="h-4 w-4" />,
                  onClick: (row: any) => navigate(`/patient-careplan/${row?._id}`),
                  isShow: () => true,
                },
              ]}
              clickRow={() => { }}
              params={{}}
              url="/portal-access/shared-careplans"
              key={"shared-careplans"}
            />
          </TabsContent>
          <TabsContent value="treatment-plan" className="mt-4">
            <TableWithSearch
              apiDataKey="treatmentPlan"
              isColumnSelection
              columnPreferenceKey="portal-shared-treatment-plans"
              columnHeaders={treatmentPlanHeaders}
              data={treatmentPlans || []}
              viewData={(treatmentPlans || []).map((v: any) => ({
                ...v,
                patientId: `${v?.patientId?.firstName} ${v?.patientId?.lastName}`,
                careTakerId: `${v?.careTakerId?.firstName} ${v?.careTakerId?.lastName}`,
                deviceType: Array.isArray(v?.deviceType)
                  ? v.deviceType.map((dt: string) => dt.replace(/_/g, " ")).join(", ")
                  : v?.deviceType?.replace(/_/g, " ") || "N/A",
                status: v?.status?.replace(/_/g, " ") || "",
              }))}
              setData={setTreatmentPlans}
              fetchCondition={IS_PORTAL_ACCESS && user && portalTab === "treatment-plan"}
              isIncludeSearch
              searchPlaceholder="Search Shared Treatment Plans..."
              filters={[]}
              filtersOptions={[]}
              setFilters={() => { }}
              heading="Shared Treatment Plans"
              isActionColumn={false}
              handleAction={() => { }}
              moreActions={[
                {
                  label: "View",
                  icon: <Eye className="h-4 w-4" />,
                  onClick: (row: any) =>
                    navigate(
                      `/treatment-plan/${row?.patientId?._id}/${row?._id}?view=true`
                    ),
                  isShow: () => true,
                },
              ]}
              clickRow={() => { }}
              params={{ patientId: portalPatientIds.length ? portalPatientIds.join(",") : "" }}
              url="/treatment-plan"
              key={"shared-treatment-plans"}
            />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-8">
      <div>
        <h2 className="text-2xl md:text-3xl font-semibold md:font-bold tracking-tight">
          {displayName || user?.name ? `Welcome, ${displayName || user?.name}` : "Welcome Back"}
        </h2>
        <p className="text-muted-foreground mt-0">
          {/* {user?.role === "caretaker"
              ? "Here's an overview of your assigned patients"
              : "Here's an overview of your chronic care management activities"} */}
        </p>
      </div>

      {/* Stats Section */}
      <LoaderWrapper
        loading={statsLoading}
        error={statsErr} //statsErr
        customLoaderComponent={<DashboardcardShimmer count={4} />}
      >
        <div
          className={clsx(
            stats.length == 4
              ? "grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
              : "grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5",
          )}
        >
          {stats.map((stat, index) => (
            <>
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card
                  className={`overflow-hidden ${stat.clickable
                      ? "cursor-pointer hover:shadow-lg transition-shadow"
                      : ""
                    }`}
                  onClick={() => {
                    console.log({stat});
                    if (stat.clickable && stat.navigateTo) {
                      navigate(stat.navigateTo, {
                        state: stat?.stateData || {},
                      });
                    }
                  }}
                >
                  <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {stat.title}
                    </CardTitle>
                    <div className={`p-2 rounded-lg ${stat.bgColor} shrink-0`}>
                      <stat.icon className={`w-4 h-4 ${stat.color}`} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold">
                      {stat.value}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {stat.clickable ? "Click to view" : " "}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </>
          ))}
        </div>
      </LoaderWrapper>

      {/* Caretakers Section */}
      {isAuthorized("canViewCaretakerManagement") && (
        <Card className='gap-4 flex flex-col h-200 overflow-hidden'>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <HeartHandshake className="w-5 h-5" />
                Caretakers Management
                {/* {selectedOrganization && (
                  <span className="text-sm font-normal text-muted-foreground">
                    - {selectedOrganization.name}
                  </span>
                )} */}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto">
            <LoaderWrapper
              loading={caretakerLoading}
              error={caretakerError}
              customLoaderComponent={<HorizontalCardShimmer count={4} />}
            >
              {!selectedOrgId ? (
                <p className="text-center text-muted-foreground py-8">
                  Please select an organization to view caretakers.
                </p>
              ) : caretakers && caretakers.length > 0 ? (
                <div className="space-y-4">
                  {caretakers.map((caretaker, index) => (
                    <motion.div
                      key={caretaker._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() =>
                        // navigate(`/patients`, {
                        //   state: {
                        //     filters: {
                        //       status: ["active"],
                        //       caretakers: caretaker._id,
                        //     },
                        //   },
                        // })
                        navigate(`/caretakers/${caretaker?._id}`)
                      }
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-base">
                              {caretaker.firstName} {caretaker.lastName}
                            </p>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${caretaker.status === "active"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                                }`}
                            >
                              {caretaker.status}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              <span>{caretaker.email}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              <span>
                                {formatUSPhoneNumber(caretaker.phone)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2 bg-primary/10 px-3 py-2 rounded-lg">
                            <Users className="w-4 h-4 text-primary" />
                            <div className="flex gap-4">
                              <div>
                                <p className="text-2xl font-bold text-primary">
                                  {caretaker.activePatientsCount || 0}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Active
                                </p>
                              </div>
                              {/* <div className="border-l border-primary/20 pl-4">
      <p className="text-2xl font-bold text-amber-600">{caretaker.pendingPatientCount || 0}</p>
      <p className="text-xs text-muted-foreground">Pending</p>
    </div> */}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No caretakers found for this organization. Add caretakers to
                  get started.
                </p>
              )}
            </LoaderWrapper>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
